import api from '../axios'
import { productApi } from './product'

const STORE_PRODUCTS_DATASET_URL = '/data/products.json'

/**
 * @description 商城模块 API 集合
 */
export const storeApi = {
  /**
   * @description 获取商城产品列表及库存信息（页面加载，失败静默）
   * @returns {Promise<Array>} 商城产品列表
   */
  getStoreProducts: () => api.get('/store/products', { showError: false }),

  /**
   * @description 获取购物车详情
   * @returns {Promise<Object>} 购物车信息
   */
  getCart: () => api.get('/store/cart'),

  /**
   * @description 产品对比
   * @param {Object} params - 请求参数
   * @param {boolean} params.onlyShowDifferences - 是否仅显示差异
   * @param {Array<number>} params.prodIds - 产品ID数组
   * @returns {Promise<Object>} 对比结果
   */
  compareProducts: (params, config = {}) => api.post('/prod/comparison', params, config),

  /**
   * @description 搜索产品列表
   * @param {Object} params - 请求参数
   * @param {number} params.current - 当前页码
   * @param {number} params.size - 每页大小
   * @param {number} params.sort - 排序方式
   * @param {Array<number>} params.prodIds - 产品ID数组（可选）
   * @param {number} params.primaryCategoryId - 主分类ID
   * @returns {Promise<Object>} 搜索结果
   */
  searchProducts: (params) => productApi.searchProducts(params),

  /**
   * @description 全局搜索（仅商品 · 仅已上架且未退市 · 品类/型号/名称/简介/描述/参数 模糊）
   * @param {Object} params - 请求参数
   * @param {string} params.keyword - 搜索关键词
   * @param {boolean} params.isClick - 是否点击搜索
   * @param {number} [params.size] - 候选商品数量上限（默认 30）
   * @returns {Promise<Array>} 形如 [{ serviceType: 1, globalSearchList: [{ serviceId, serviceName }] }]
   */
  globalSearch: (params) => api.post('/p/store/search/global', params),

  /**
   * @description 获取商城分类信息（页面加载，失败静默）
   * @returns {Promise<Object>} 分类信息
   */
  getCategoryInfo: () => api.get('/prod/prod/getCategoryProd', { showError: false }),

  /**
   * @description 获取商城产品参数静态数据集地址
   * @returns {string} 商品参数数据集地址
   */
  getProductsDatasetUrl: () => STORE_PRODUCTS_DATASET_URL,

  /**
   * @description 获取活动列表
   * @param {Object} params - 查询参数
   * @param {string} [params.prodModel] - 商品型号模糊查询
   * @param {string} [params.keyword] - 商品名称/型号模糊搜索关键词
   * @param {Array} [params.prodIds] - 商品ID列表
   * @param {number} [params.promotionType] - 活动类型：1=满量特价 2=满量折扣 3=买赠 4=搭配套餐 5=组合促销
   * @param {number} [params.salesOrganization] - 销售组织
   * @param {number} [params.page] - 当前页码
   * @param {number} [params.pageSize] - 每页条数
   * @param {Object} [config] - axios 请求配置（如 concurrencyKey）
   * @returns {Promise<Object>} 活动列表
   */
  getPromotions: (params, config = {}) =>
    api.get('/store/promotions', { params, showError: false, ...config }),

  /**
   * @description 获取活动统计
   * @param {Object} params - 查询参数
   * @param {number} [params.salesOrganization] - 销售组织：1=TDC 2=IPC
   * @returns {Promise<Object>} 活动统计
   */
  getPromotionStatistic: (params) =>
    api.get('/store/promotions/statistic', { params, showError: false }),

  /**
   * @description 按活动 ID 查询活动详情
   * @param {string|number} promotionId - 活动 ID（promotion.id）
   * @returns {Promise<Object|null>} 活动详情
   */
  getPromotionDetail: (promotionId) =>
    api.get(`/store/promotions/${encodeURIComponent(String(promotionId))}`, { showError: false }),

  /**
   * @description 按商品 ID 查询活动详情（进行中优先，其次未开始）
   * @param {string|number} productId - 商品 ID（prod_id）
   * @returns {Promise<Object|null>} 活动详情，无活动时为 null
   */
  getPromotionByProduct: (productId) =>
    api.get(`/store/promotions/by-product/${encodeURIComponent(String(productId))}`, {
      showError: false,
    }),
}
